import { businessTypeForLink } from "./trade-links.js";
/** The longest tag kept: an ad's names are free text, and a pasted paragraph is not a campaign. */
export const UTM_MAX = 100;
/** The link parameter that names a trade: `?type=repair-shop`. */
export const LINK_TYPE_PARAM = "type";
/** What Play reports for an install nobody's link brought: the panel's «Orgánico Play». */
export const ORGANIC_PLAY = { source: "google-play", medium: "organic" };
/**
 * What Play reports when it cannot tell what brought the install -- a store
 * link opened inside another app's browser, typically. Not a campaign of
 * that name: the panel files it under «Orgánico Play» with Play's own.
 */
export const PLAY_SOURCE_NOT_SET = "(not set)";
const META_DATA_MAX = 4096;
const META_NONCE_MAX = 64;
function hexText(value, max) {
    return typeof value === "string" && value.length > 0 && value.length <= max && /^[0-9a-f]+$/i.test(value);
}
/** Meta's sealed block when `content` is its whole JSON, null for anything else (including a JSON cut short). */
export function metaReferrerOf(content) {
    if (content === null || content === undefined)
        return null;
    try {
        const source = JSON.parse(content)?.source;
        if (source === null || source === undefined || typeof source !== "object")
            return null;
        const { data, nonce } = source;
        return hexText(data, META_DATA_MAX) && hexText(nonce, META_NONCE_MAX) ? { data, nonce } : null;
    }
    catch {
        return null;
    }
}
/** True for a Meta JSON that arrived cut short: it looks like Meta's but does not open as one. */
export function isCutMetaContent(content) {
    return typeof content === "string" && content.startsWith('{"app"') && metaReferrerOf(content) === null;
}
/**
 * The pairs of a query string, decoded, the first of each name kept; a
 * leading `?` is optional and a pair that does not decode says nothing.
 * Written here rather than read with URLSearchParams, which React Native
 * implements only in part. The answer has no prototype: `toString` or
 * `constructor` is a name the query carries or nothing.
 */
export function readQuery(search) {
    const pairs = Object.create(null);
    const text = search.startsWith("?") ? search.slice(1) : search;
    for (const pair of text.split("&")) {
        if (pair === "")
            continue;
        const at = pair.indexOf("=");
        const rawName = at === -1 ? pair : pair.slice(0, at);
        const rawValue = at === -1 ? "" : pair.slice(at + 1);
        try {
            const name = decodeURIComponent(rawName.replace(/\+/g, " "));
            const value = decodeURIComponent(rawValue.replace(/\+/g, " "));
            if (pairs[name] === undefined)
                pairs[name] = value;
        }
        catch {
            // A malformed escape: this pair says nothing, the others still do.
        }
    }
    return pairs;
}
function tag(raw) {
    if (raw === null || raw === undefined)
        return null;
    // Trimmed, cut, and trimmed at the end again: a cut can land on a space.
    const value = raw.trim().slice(0, UTM_MAX).trimEnd();
    return value === "" ? null : value;
}
/** A link's tags, cleaned; null when none of them says anything. */
export function utmOf(values) {
    const utm = {
        source: tag(values.source),
        medium: tag(values.medium),
        campaign: tag(values.campaign),
        content: tag(values.content),
    };
    return utm.source === null && utm.medium === null && utm.campaign === null && utm.content === null ? null : utm;
}
/** The four tags by their names in a link, in the order a link writes them. */
const UTM_PARAMS = [
    ["source", "utm_source"],
    ["medium", "utm_medium"],
    ["campaign", "utm_campaign"],
    ["content", "utm_content"],
];
/** The tags of a query string (`location.search`, a referrer). */
export function utmFromQuery(search) {
    const query = readQuery(search);
    const values = {};
    for (const [key, name] of UTM_PARAMS)
        values[key] = query[name];
    return utmOf(values);
}
/** Hosts (or host prefixes ending in a dot) of the search engines: their visits are organic, the rest referral. */
const SEARCH_ENGINES = ["google.", "bing.com", "duckduckgo.com", "yahoo.", "ecosia.org", "search.brave.com"];
/**
 * Where a sign-in comes back from, never where a visit came from: the return
 * pages load right after a POST from the provider, so their referrer is its host.
 */
const IDENTITY_PROVIDERS = ["accounts.google.com", "appleid.apple.com"];
function bareHost(host) {
    const lower = host.toLowerCase();
    return lower.startsWith("www.") ? lower.slice(4) : lower;
}
/**
 * The site a visit came from, as tags, when the link carried none: only its
 * host is kept. Null for this site's own pages (`ownHost`, its
 * subdomains and its parents), the identity providers, an empty or broken referrer, and anything but http(s).
 * Regex rather than URL, which Hermes implements only in part.
 */
export function utmFromReferrer(referrer, ownHost) {
    const match = /^https?:\/\/([^/?#:]+)/i.exec(referrer.trim());
    if (match === null || match[1] === undefined)
        return null;
    const host = bareHost(match[1]);
    const own = bareHost(ownHost);
    if (host === own || host.endsWith(`.${own}`) || own.endsWith(`.${host}`))
        return null;
    if (IDENTITY_PROVIDERS.includes(host))
        return null;
    const organic = SEARCH_ENGINES.some((engine) => (engine.endsWith(".") ? host.startsWith(engine) : host === engine));
    return utmOf({ source: host, medium: organic ? "organic" : "referral" });
}
/**
 * The tags as a query string to carry on (`utm_source=…&…`, no `?`), each
 * encoded, or "" when there are none: what `utmFromQuery` reads back whole.
 */
export function utmQuery(utm) {
    if (utm === null)
        return "";
    return UTM_PARAMS.flatMap(([key, name]) => {
        const value = utm[key];
        return value === null ? [] : [`${name}=${encodeURIComponent(value)}`];
    }).join("&");
}
/** The trade a query string names, as its slug, only when a vertical answers to it. */
export function linkTypeFromQuery(search) {
    const slug = readQuery(search)[LINK_TYPE_PARAM]?.trim();
    return slug !== undefined && businessTypeForLink(slug) !== null ? slug : null;
}
/**
 * The install referrer as Play hands it (`getInstallReferrerAsync`):
 * `utm_source=…&type=…`, decoded once. A link built by hand may arrive
 * encoded once or twice more, whole or past its first `=`: while an encoded
 * `=` is left, it is decoded again. An encoded `&` alone stays, inside its
 * tag. Anything that is not a query string is nothing: the first launch
 * never fails on it.
 */
export function parseInstallReferrer(raw) {
    const nothing = { utm: null, linkType: null, meta: null };
    if (raw === null || raw === undefined)
        return nothing;
    let text = raw.trim();
    for (let round = 0; round < 2 && /%3D/i.test(text); round++) {
        try {
            text = decodeURIComponent(text);
        }
        catch {
            return nothing;
        }
    }
    if (!text.includes("="))
        return nothing;
    const meta = metaReferrerOf(readQuery(text)["utm_content"]);
    const utm = utmFromQuery(text);
    if (meta === null)
        return { utm, linkType: linkTypeFromQuery(text), meta };
    // Meta's block is not a tag: the content stays empty, and the other tags stand.
    return { utm: utm === null ? null : utmOf({ ...utm, content: null }), linkType: linkTypeFromQuery(text), meta };
}
/** The answers to «¿Cómo nos conociste?», as stored: fixed slugs, in the order they are offered. */
export const HEARD_FROM = ["instagram", "facebook", "tiktok", "google", "referral", "shop", "other"];
export const HEARD_FROM_QUESTION = "¿Cómo nos conociste?";
export const HEARD_FROM_LABELS = {
    instagram: "Instagram",
    facebook: "Facebook",
    tiktok: "TikTok",
    google: "Google",
    // Short enough for a tile half the phone wide.
    referral: "Me lo recomendaron",
    shop: "Tienda de otro negocio",
    other: "Otro",
};
